import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ContentService } from "../../services/content.service";
import { JourneyQuestion } from "../../models/journey-question.model";
import { ResponseService } from "../../services/response.service";
import { DonorJourneySection } from "../../models/donor-journey-section.model";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { AppointmentModalComponent } from "../appointment-modal/appointment-modal.component";
import { Appointment } from "../../models/appointment.model";
import { ConfirmModalComponent } from "../confirm-modal/confirm-modal.component";
import { NotesModalComponent } from "../notes-modal/notes-modal.component";
import { NotesService } from "../../services/notes.service";
import { Note, NoteType } from "../../models/note.model";
import { UploadDocumentModalComponent } from "../upload-document-modal/upload-document-modal.component";
import { S3Document } from "../../models/s3-document.model";
import { EpicLookupModalComponent } from "../epic-lookup-modal/epic-lookup-modal.component";
import { Practitioner } from "../../models/practitioner.model";

type TranslationState = {
  translatedText?: string;
  sourceLocale?: string;
  targetLocale: string;
  loading?: boolean;
  error?: string;
};

const NOTE_TITLE_TO_GENERATION_TYPE: Record<string, NoteType> = {
  "transplant nephrologist": "nephrology",
  "transplant surgeon": "surgeon",
  "transplant coordinator": "coordinator",
  "social worker": "social_work",
  dietician: "dietician",
};

const TRANSLATION_EXCLUDED_EXACT_QUESTIONS = new Set([
  "email",
  "e mail",
  "correo",
  "correo electronico",
  "email address",
  "height",
  "feet",
  "ft",
  "pies",
  "inches",
  "inch",
  "in",
  "pulgadas",
  "pulgada",
  "weight",
  "pounds",
  "pound",
  "lbs",
  "lb",
  "peso",
  "libras",
  "libra",
  "name",
  "nombre",
  "apellido",
]);

const TRANSLATION_EXCLUDED_QUESTION_PHRASES = [
  "first name",
  "last name",
  "full name",
  "nombre completo",
  "correo electronico",
  "email address",
];

const TRANSLATION_EXCLUDED_QUESTION_TOKENS = new Set([
  "name",
  "nombre",
  "apellido",
  "email",
  "correo",
]);


@Component({
  selector: "app-journey-section",
  templateUrl: "./journey-section.component.html",
  styleUrl: "./journey-section.component.scss",
})
export class JourneySectionComponent implements OnInit {
  @Input() section!: DonorJourneySection; // Replace with JourneySection type
  @Input() stepNumber!: number;
  @Input() isFirstSection = false;
  @Input() isLastSection = false;
  @Input() isAdmin = false;
  @Input() statusOptions: string[] = [
    "Not Started",
    "In Progress",
    "Completed",
    "Under Review",
    "Not Scheduled",
    "Scheduled",
    "Approved",
    "Denied",
  ];
  @Input() pageUrl = "";
  @Input() expanded = false;
  @Input() isComprehensiveQuestionnaireCompleted = true;
  @Input() donorLocale = "";

  @ViewChild("journeySection") journeySection!: ElementRef;
  @ViewChild("expandedLine") expandedLine!: ElementRef;

  // TODO: Make these dynamic!!!!!
  initialQuestionnaireSection = "JS_5e1deb99-d48f-4924-9dd7-dfe87dff922f";
  comprehensiveQuestionnaireSection = "JS_28be6f2a-814f-47f6-9942-29a9d37565df";
  providerSections = ["transplant nephrologist", "transplant surgeon", "social worker", "dietician"]
  s3BucketName = "graftdevdocuments";
  loadingDocId: string | null = null;

  questionnaireResponses: JourneyQuestion[] = [];
  paginatedResponses: JourneyQuestion[] = [];
  filteredResponses: JourneyQuestion[] = [];
  searchQuery = "";
  currentPage = 1;
  pageSize = 10; // Items per page
  totalResponses = 0;
  noteContent = "";
  summaryContent = "";
  quillModules: any;
  selectedStatus = "Not Scheduled";
  isStatusUpdating = false;
  isFetchingDocuments = false;
  deletingDocId: string | null = null;
  isQuestionnaireSection = false;
  deletingPractitioner = false;
  translatedAnswers: Record<string, TranslationState> = {};

  constructor(
    public elementRef: ElementRef,
    private contentService: ContentService,
    private responseService: ResponseService,
    private notesService: NotesService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.quillModules = {
      toolbar: false, // Disable the toolbar for read-only mode
    };

    if (!this.pageUrl.includes("hospital/donor")) {
      this.isAdmin = false;
    }
    if (this.section.questionnaireId) {
      this.getResposnesForQuestionnaire(this.section.questionnaireId);
    }

    if (this.isAdmin) {
      this.setNoteContent("Note", this.section.notes);
      this.setNoteContent("Summary", this.section.notes);
      this.checkQuestionnaireSection();
    }

    
  }

  toggleExpand(): void {
    this.expanded = !this.expanded;

    if (this.journeySection && this.expandedLine) {
      // Waits till the div is rendered
      setTimeout(() => {
        this.calculateAndSetVerticalLineHeight();
      }, 500);
    }
  }

  calculateAndSetVerticalLineHeight(){
    let journeyHeight = this.journeySection.nativeElement.clientHeight; // Get height
        journeyHeight += 100;
        this.expandedLine.nativeElement.style.height = `${journeyHeight}px`;
  }

  checkQuestionnaireSection(){
    this.isQuestionnaireSection = 
    this.section.sectionId === this.initialQuestionnaireSection || 
    this.section.sectionId === this.comprehensiveQuestionnaireSection;
    
  }

  private resolveGenerationNoteType(title?: string): NoteType | undefined {
    if (!title) {
      return undefined;
    }

    const normalizedTitle = title.trim().toLowerCase();
    return NOTE_TITLE_TO_GENERATION_TYPE[normalizedTitle];
  }

  addPractitioner(section: DonorJourneySection){
    const practitionerModalRef = this.modalService.open(EpicLookupModalComponent, {
      centered: true,
      size: "lg",
    })

    practitionerModalRef.componentInstance.type = "Provider";
    practitionerModalRef.componentInstance.userJourneySectionId = section.userJourneySectionId;

    practitionerModalRef.result
    .then((result) => {
      const practitioner: Practitioner = {
        linkId: result.response.id,
        ...result.epicRecord
      }
      section.practitioners.push(practitioner);
     
    })
    .catch((error) => {
      console.log("Modal dismissed:", error);
    });
  }

  updatePractitioner(section: DonorJourneySection){
     const practitionerModalRef = this.modalService.open(EpicLookupModalComponent, {
      centered: true,
      size: "lg",
    })

    practitionerModalRef.componentInstance.type = "Provider";
    practitionerModalRef.componentInstance.userJourneySectionId = section.userJourneySectionId;
    practitionerModalRef.componentInstance.linkId = section.practitioners[0].linkId;

    practitionerModalRef.result
    .then((result) => {
      const practitioner: Practitioner = {
        linkId: result.response.id,
        ...result.epicRecord
      }
      section.practitioners.splice(0, 1);
      section.practitioners.push(practitioner);
     
    })
    .catch((error) => {
      console.log("Modal dismissed:", error);
    });
  }

  deletePractitioner(section: DonorJourneySection){
     this.deletingPractitioner = true;
    this.contentService.removeProviderFromSection(section.practitioners[0].linkId).subscribe({
      next: (response)=>{
        this.deletingPractitioner = false;
        section.practitioners.splice(0, 1);
      },
      error: (err)=>{
        console.log(err);
      }
    })
  }

  addAppointment(section: DonorJourneySection) {
    const modalRef = this.modalService.open(AppointmentModalComponent, {
      centered: true,
      size: "md",
    });
    modalRef.componentInstance.section = section;
    modalRef.componentInstance.mode = "add";

    modalRef.result
      .then((result) => {
        this.refreshAppointments(section.sectionId, result.userId);
        this.updateStatus("Scheduled");
      })
      .catch((error) => {
        console.log("Modal dismissed:", error);
      });
  }

  updateAppointment(appointment: Appointment, section: any) {
    const modalRef = this.modalService.open(AppointmentModalComponent, {
      centered: true,
      size: "md",
    });
    // Pass the existing appointment data and section data to the modal
    modalRef.componentInstance.section = section;
    modalRef.componentInstance.appointment = appointment;
    modalRef.componentInstance.appointmentForm.patchValue({
      appointmentId: appointment.appointmentId,
      date: appointment.date,
      time: this.formatTime(appointment.time),
      location: appointment.location,
      notes: appointment.notes,
    });
    modalRef.componentInstance.mode = "edit";

    modalRef.result
      .then((result) => {
        console.log("Updated appointment:", result);
        this.refreshAppointments(section.sectionId, result.userId);
        
      })
      .catch((error) => {
        console.log("Modal dismissed:", error);
      });
  }

  deleteAppointment(appointment: Appointment, section: any) {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      centered: true,
      size: "sm",
    });

    // Pass details to the confirmation modal (optional)
    modalRef.componentInstance.message = `Are you sure you want to delete this appointment?`;
    modalRef.componentInstance.appointmentId = appointment.appointmentId;

    modalRef.result
      .then((result) => {
        this.refreshAppointments(section.sectionId, section.userId);
      })
      .catch((dismissReason) => {
        console.log("Modal dismissed:", dismissReason);
      });
  }

  refreshAppointments(sectionId: string, userId: string) {
    this.contentService
      .getAppointmentsBySectionIdAndUserId(sectionId, userId)
      .subscribe({
        next: (data: { appointments: Appointment[] }) => {
          if (this.section && data?.appointments?.length > 0) {
            // Map data to ensure appointmentId is always a string
            this.section.appointments = data.appointments.sort((a, b) => {
              const dateTimeA = new Date(`${a.date}T${a.time}`).getTime();
              const dateTimeB = new Date(`${b.date}T${b.time}`).getTime();
              return dateTimeA - dateTimeB; // Sort ascending
            });
          } else {
            this.section.appointments = [];
            this.updateStatus("Not Scheduled");
          }
          this.calculateAndSetVerticalLineHeight();
        },
        error: (error: any) => {
          console.error("Error refreshing appointments:", error);
        },
      });
  }

  private formatTime(time: string): string {
    // Check if the time is in the format HH:mm:ss
    if (time && time.includes(":")) {
      const [hours, minutes] = time.split(":"); // Extract hours and minutes
      return `${hours}:${minutes}`; // Return formatted time as HH:mm
    }
    return time; // Return original value if it's already in the correct format
  }

  hasNotesOfType(type: "Note" | "Summary", notes: any[]): boolean {
    return notes.some((note) => note.noteType === type);
  }
  
  addOrEditNote(type: "Note" | "Summary", section: DonorJourneySection) {
    const foundNote = section.notes.find((note) => note.noteType === type);

    const modalRef = this.modalService.open(NotesModalComponent, {
      size: "xl",
      centered: true,
      modalDialogClass: "notes-modal-dialog",
    });
    modalRef.componentInstance.editorContent = foundNote?.content;
    modalRef.componentInstance.noteType = type;
    modalRef.componentInstance.noteId = foundNote?.noteId; // Pass initial content if needed
    modalRef.componentInstance.section = section;
    modalRef.componentInstance.isComprehensiveQuestionnaireCompleted =
      this.isComprehensiveQuestionnaireCompleted;
    // Pass a generation note type for templating based on section title
    const generationNoteType = this.resolveGenerationNoteType(section?.title);
    modalRef.componentInstance.generationNoteType = generationNoteType;

    modalRef.result
      .then((result) => {
        if (result) {
          if (result.action == "edit") {
            if (foundNote) {
              foundNote.content = result.content;
              if (type == "Note") {
                this.noteContent = result.content;
              } else {
                this.summaryContent = result.content;
              }
            }
          } else if (result.action == "create") {
            console.log("note created");
            section.notes.push(result.note);
            if (type == "Note") {
              this.noteContent = result.content;
            } else {
              this.summaryContent = result.content;
            }
          }
          setTimeout(() => {
            this.calculateAndSetVerticalLineHeight();
          }, 500);
        }
      })
      .catch((error) => {
        console.log("Modal dismissed:", error);
      });
  }

  uploadDocument() {
    const modalRef = this.modalService.open(UploadDocumentModalComponent, {
      size: "md",
      centered: true,
    });

    modalRef.componentInstance.journeySectionId =
      this.section.userJourneySectionId;
    modalRef.componentInstance.sectionId = this.section.sectionId;
    modalRef.componentInstance.userId = this.section.userId;
    modalRef.componentInstance.s3BucketName = this.s3BucketName;

    modalRef.result
      .then((result) => {
        this.isFetchingDocuments = true;
        if (result) {
          this.contentService
            .getDocumentsByUserJourneySection(this.section.userJourneySectionId)
            .subscribe({
              next: (s3Documents: S3Document[]) => {
                this.section.documents = s3Documents;
                this.isFetchingDocuments = false;
              },
              error: (err) => {
                console.error("Error getting documents: ", err);
                this.isFetchingDocuments = false;
              },
            });
        }
      })
      .catch((error) => {
        console.log("Modal dismissed:", error);
      });
  }

  deleteDocument(doc: S3Document) {
    this.deletingDocId = doc.id;
    this.contentService.deleteDocument(doc.id).subscribe({
      next: (res) => {
        this.deletingDocId = null;
        this.isFetchingDocuments = true;
        this.contentService
          .getDocumentsByUserJourneySection(this.section.userJourneySectionId)
          .subscribe({
            next: (s3Documents: S3Document[]) => {
              console.log("docs: ", s3Documents);
              this.section.documents = s3Documents;
              this.isFetchingDocuments = false;
            },
            error: (err) => {
              console.error("Error getting documents: ", err);
              this.isFetchingDocuments = false;
            },
          });
      },
      error: (err) => {
        this.deletingDocId = null;
        console.error("Error generating download URL:", err);
      },
    });
  }

  toggleShareWithDonor(doc: S3Document, event: Event) {
    const checked = (event.target as HTMLInputElement).checked; // Get checkbox state

    doc.sharedWithDonor = checked; // Optimistic UI update
    doc.isLoading = true;

    // Call API to update shareWithDonor in the database
    this.contentService.updateShareWithDonor(checked, doc.id).subscribe({
      next: () => {
        doc.isLoading = false;
      },
      error: (err) => {
        console.error("Error updating document share status:", err);
        doc.sharedWithDonor = !checked; // Revert UI state on failure
        doc.isLoading = false;
      },
    });
  }

  generateDownloadUrl(doc: S3Document) {
    this.loadingDocId = doc.id;

    this.contentService.generateDownloadUrl(doc.fileName).subscribe({
      next: ({ downloadUrl }) => {
        window.open(downloadUrl, "_blank");
        this.loadingDocId = null;
      },
      error: (err) => {
        console.error("Error generating download URL:", err);
        this.loadingDocId = null;
      },
    });
  }

  setNoteContent(type: "Note" | "Summary", notes: Note[]): void {
    const foundNote = notes.find((note) => note.noteType === type);
    foundNote?.content || ""; // Return the found note or null if not found
    if (type == "Note") {
      this.noteContent = foundNote?.content || "";
    } else if (type == "Summary") {
      this.summaryContent = foundNote?.content || "";
    }
  }

  updateStatus(status: string) {
    this.selectedStatus = status;
    // Trigger a service call or emit an event to update the status in the backend
    this.saveStatusToBackend();
  }

  saveStatusToBackend() {
    this.isStatusUpdating = true;
    this.contentService
      .updateJourneySectionStatusByUserIdForAdmin(
        this.section.sectionId,
        this.selectedStatus,
        this.section.userId
      )
      .subscribe({
        next: (response) => {
          this.isStatusUpdating = false;
          this.section.currentStatus = this.selectedStatus;
        },
        error: (err) => {},
      });
  }

  getResposnesForQuestionnaire(questionnaireId: string) {
    this.responseService
      .getQuestionnaireResponsesByQuestionnaireId(questionnaireId)
      .subscribe({
        next: (responses: JourneyQuestion[]) => {
          this.questionnaireResponses = this.combineHeightResponses(responses);
          this.totalResponses = this.questionnaireResponses.length;
          this.filteredResponses = this.questionnaireResponses;
          this.updatePaginatedResponses();
        },
        error: (error) => {
          console.log(error);
        },
      });
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.updatePaginatedResponses();
  }

  // Calculate the current page's responses
  updatePaginatedResponses() {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;

    this.paginatedResponses = this.filteredResponses.slice(
      startIndex,
      endIndex
    );
    this.totalResponses = this.filteredResponses.length;
  }

  filterResponses() {
    const query = this.searchQuery.toLowerCase().trim();
    if (!query) {
      this.filteredResponses = [...this.questionnaireResponses];
      this.updatePaginatedResponses();
    } else {
      this.filteredResponses = this.questionnaireResponses.filter(
        (response) =>
          response.sectionName.toLowerCase().includes(query) ||
          response.questionText.toLowerCase().includes(query) ||
          this.getAnswerDisplayText(response).toLowerCase().includes(query)
      );
      this.currentPage = 1;
      this.updatePaginatedResponses();
    }
  }

  getAnswerDisplayText(response: JourneyQuestion): string {
    return String(response.answerText || "").trim();
  }

  getTranslatedDisplayText(response: JourneyQuestion): string {
    return String(
      this.getTranslationState(response)?.translatedText || ""
    ).trim();
  }

  canTranslateResponse(response: JourneyQuestion): boolean {
    return (
      this.isAdmin &&
      this.isSpanishDonorLocale() &&
      this.isTranslatableQuestionnaireSection() &&
      this.hasTranslatableAnswer(response) &&
      this.isOpenTextResponse(response) &&
      !this.isTranslationExcludedResponse(response)
    );
  }

  translateResponse(response: JourneyQuestion): void {
    if (!this.canTranslateResponse(response)) {
      return;
    }

    const key = this.getTranslationKey(response);
    const existingTranslation = this.translatedAnswers[key];

    if (existingTranslation?.translatedText) {
      return;
    }

    this.translatedAnswers[key] = {
      targetLocale: "en",
      loading: true,
    };

    this.contentService
      .translateText({
        sourceLocale: "es",
        targetLocale: "en",
        text: this.getAnswerDisplayText(response),
        context:
          "Living kidney donor questionnaire open-text answer",
      })
      .subscribe({
        next: (translation) => {
          this.translatedAnswers[key] = {
            translatedText: translation.translatedText,
            sourceLocale: translation.sourceLocale,
            targetLocale: translation.targetLocale || "en",
          };
        },
        error: () => {
          this.translatedAnswers[key] = {
            targetLocale: "en",
            error: "Translation failed. Please try again.",
          };
        },
      });
  }

  getTranslationState(response: JourneyQuestion): TranslationState | null {
    return this.translatedAnswers[this.getTranslationKey(response)] || null;
  }

  private hasTranslatableAnswer(response: JourneyQuestion): boolean {
    return !!response.answerText && response.answerText.trim().length > 0;
  }

  private isOpenTextResponse(response: JourneyQuestion): boolean {
    const questionType = (response.questionType || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

    return [
      "short",
      "long",
      "text",
      "textarea",
      "open_text",
      "short_text",
      "long_text",
    ].includes(questionType);
  }

  private isTranslationExcludedResponse(response: JourneyQuestion): boolean {
    const questionText = this.normalizeText(response.questionText).trim();
    const questionTokens = questionText.split(/\s+/);

    return (
      TRANSLATION_EXCLUDED_EXACT_QUESTIONS.has(questionText) ||
      questionTokens.some((token) =>
        TRANSLATION_EXCLUDED_QUESTION_TOKENS.has(token)
      ) ||
      TRANSLATION_EXCLUDED_QUESTION_PHRASES.some((phrase) =>
        questionText.includes(phrase)
      )
    );
  }

  private isSpanishDonorLocale(): boolean {
    return this.donorLocale.trim().toLowerCase().startsWith("es");
  }

  private combineHeightResponses(responses: JourneyQuestion[]): JourneyQuestion[] {
    const consumedQuestionIds = new Set<string>();

    return responses.reduce<JourneyQuestion[]>((combinedResponses, response) => {
      if (response.questionId && consumedQuestionIds.has(response.questionId)) {
        return combinedResponses;
      }

      if (
        this.isInchesResponse(response) &&
        responses.some(
          (candidate) =>
            candidate.questionId !== response.questionId &&
            candidate.sectionName === response.sectionName &&
            this.isFeetResponse(candidate)
        )
      ) {
        return combinedResponses;
      }

      if (!this.isFeetResponse(response)) {
        combinedResponses.push(response);
        return combinedResponses;
      }

      const inchesResponse = responses.find(
        (candidate) =>
          candidate.questionId !== response.questionId &&
          candidate.sectionName === response.sectionName &&
          this.isInchesResponse(candidate)
      );

      if (!inchesResponse) {
        combinedResponses.push(response);
        return combinedResponses;
      }

      if (response.questionId) {
        consumedQuestionIds.add(response.questionId);
      }

      if (inchesResponse.questionId) {
        consumedQuestionIds.add(inchesResponse.questionId);
      }

      combinedResponses.push({
        ...response,
        questionId: [response.questionId, inchesResponse.questionId]
          .filter(Boolean)
          .join("|"),
        questionText: "Height",
        answerText: this.formatHeightAnswer(
          response.answerText,
          inchesResponse.answerText
        ),
      });

      return combinedResponses;
    }, []);
  }

  private formatHeightAnswer(feet: string, inches: string): string {
    const feetValue = String(feet || "").trim();
    const inchesValue = String(inches || "").trim();

    if (!feetValue && !inchesValue) {
      return "";
    }

    return `${feetValue || 0} ft ${inchesValue || 0} in`;
  }

  private isFeetResponse(response: JourneyQuestion): boolean {
    const questionText = this.normalizeText(response.questionText).trim();
    return ["feet", "ft", "pies"].includes(questionText);
  }

  private isInchesResponse(response: JourneyQuestion): boolean {
    const questionText = this.normalizeText(response.questionText).trim();
    return ["inches", "inch", "in", "pulgadas", "pulgada"].includes(
      questionText
    );
  }

  private isTranslatableQuestionnaireSection(): boolean {
    return [
      this.initialQuestionnaireSection,
      this.comprehensiveQuestionnaireSection,
    ].includes(this.section.sectionId);
  }

  private normalizeText(value: string | undefined | null): string {
    return ` ${String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim()} `;
  }

  private getTranslationKey(response: JourneyQuestion): string {
    return [
      response.questionId || response.questionText,
      response.answerText,
      "en",
    ].join("|");
  }

  formatDate(date: string, time: string): string {
    const ordinalSuffix = (day: number): string => {
      if (day > 3 && day < 21) return "th"; // Covers 11th to 13th
      switch (day % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    // Combine the date and time into a single Date object
    const dateTime = new Date(`${date}T${time}`);

    // Extract components
    const month = dateTime.toLocaleString("en-US", { month: "short" }); // "Feb"
    const day = dateTime.getDate(); // Day of the month
    const year = dateTime.getFullYear(); // Year
    const hours = dateTime.getHours(); // Hours (24-hour format)
    const minutes = dateTime.getMinutes(); // Minutes

    // Convert 24-hour format to 12-hour format
    const period = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight

    // Format the day with the ordinal suffix
    const dayWithSuffix = `${day}${ordinalSuffix(day)}`;

    // Format the minutes with leading zero if needed
    const formattedMinutes = minutes.toString().padStart(2, "0");

    // Combine all parts into the desired format
    return `${month} ${dayWithSuffix}, ${year} ${hours12}:${formattedMinutes}${period}`;
  }
}
